import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ScanLine } from "lucide-react";

/** Shared showcase primitives used by the graha and rashi detail pages. */

export const TYPE_SHOP = { mala: "/shop/malas", bracelet: "/shop/bracelets", pendant: "/shop/pendants", yantra: "/shop/yantras" };

export function firstSentences(text, n = 2) {
  if (!text) return "";
  return text.split(/(?<=\.)\s+/).slice(0, n).join(" ");
}

export function Fact({ k, v, wide = false }) {
  return (
    <div className={wide ? "col-span-2 lg:col-span-1" : ""}>
      <p className="ui text-[10px] font-medium uppercase tracking-[0.18em] text-[#f0d693]/75">{k}</p>
      <p className="mt-1.5 text-[14px] leading-[1.5] text-white/90">{v}</p>
    </div>
  );
}

/** Showcase shell: one photo on the left, copy + piece rows on the right. */
export function Showcase({ testId, image, alt, chips, eyebrow, title, deva, copy, pieces, renderPiece, cta }) {
  const availCount = pieces.filter((p) => p.status === "available").length;
  return (
    <div className="graha-showcase" data-testid={testId}>
      <div className="graha-showcase__media">
        <img src={image} alt={alt} loading="lazy" />
        <div className="absolute left-3 top-3 flex flex-wrap gap-1.5">
          {chips.map((c) => (
            <span key={c.label} className={`ui inline-flex items-center gap-1 px-2 py-1 text-[10px] font-medium uppercase tracking-[0.12em] ${c.tone}`}>
              {c.icon}
              {c.label}
            </span>
          ))}
        </div>
        <p className="ui absolute inset-x-3 bottom-3 bg-ink/70 px-2.5 py-1.5 text-[10px] leading-[1.5] tracking-[0.04em] text-wash/90 backdrop-blur">
          Type photograph — your actual piece is photographed loose before dispatch.
        </p>
      </div>
      <div className="p-6 sm:p-8">
        <p className="ui text-[10px] font-semibold uppercase tracking-[0.22em] text-gold">{eyebrow}</p>
        <div className="mt-2 flex flex-wrap items-baseline gap-x-3">
          <h3 className="text-[24px] leading-tight" style={{ fontFamily: "Fraunces, Georgia, serif" }}>{title}</h3>
          {deva && <p className="text-[15px] text-gold"><span className="deva">{deva.text}</span>{deva.roman && <span className="ui ml-1.5 italic opacity-70">{deva.roman}</span>}</p>}
        </div>
        <p className="mt-3 max-w-[var(--measure-read)] text-[14px] leading-[1.7] text-dim"
           style={{ fontFamily: "'Source Serif 4', Georgia, serif" }}>
          {copy}
        </p>
        <p className="ui mt-4 text-[11px] font-medium uppercase tracking-[0.16em] text-verify">
          {availCount} of {pieces.length} piece{pieces.length > 1 ? "s" : ""} available — each one-of-one
        </p>
        <div className="mt-3 border-b border-rule">
          {pieces.map((p) => renderPiece(p))}
        </div>
        {cta && (
          <Link
            to={cta.to}
            className="ui mt-5 inline-flex items-center gap-1.5 text-[12px] font-medium uppercase tracking-[0.16em] text-gold hover:underline"
            data-testid={`${testId}-cta`}
          >
            {cta.label} <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        )}
      </div>
    </div>
  );
}

export function GemPieceRow({ p, format }) {
  const gg = p.gemstone;
  const sold = p.status === "sold";
  return (
    <Link to={`/product/${p.slug}`} className={`graha-piece ${sold ? "graha-piece--sold" : ""}`} data-testid={`graha-piece-${p.slug}`}>
      <span className="num text-[13px] font-medium text-ink">{gg.carat} ct · {gg.ratti} ratti</span>
      <span className="text-[13px] text-dim">{gg.origin.split(" (")[0]} · {gg.treatment}</span>
      <span className="ui text-[11px] text-dim">{p.certificate?.lab} <span className="num">{p.certificate?.report_number}</span></span>
      <span className="num text-[15px] font-medium text-deep">{sold ? "Sold" : format(p.price_inr)}</span>
      <ArrowRight className="graha-piece__arrow h-3.5 w-3.5" aria-hidden="true" />
    </Link>
  );
}

export function BeadPieceRow({ p, format }) {
  const r = p.rudraksha;
  const sold = p.status === "sold";
  return (
    <Link to={`/product/${p.slug}`} className={`graha-piece ${sold ? "graha-piece--sold" : ""}`} data-testid={`graha-piece-${p.slug}`}>
      <span className="num text-[13px] font-medium text-ink">{r.diameter_mm} mm · {r.weight_g} g</span>
      <span className="text-[13px] text-dim">{r.origin === "nepal" ? "Nepal" : "Java"} · uncapped</span>
      <span className="ui inline-flex items-center gap-1 text-[11px] text-dim">
        {r.xray_image_url ? (<><ScanLine className="h-3 w-3" /> X-rayed, {r.chambers_visible} chambers</>) : "Below X-ray threshold"}
      </span>
      <span className="num text-[15px] font-medium text-deep">{sold ? "Sold" : format(p.price_inr)}</span>
      <ArrowRight className="graha-piece__arrow h-3.5 w-3.5" aria-hidden="true" />
    </Link>
  );
}

export function OtherPieceRow({ p, format }) {
  return (
    <Link to={`/product/${p.slug}`} className="graha-piece" data-testid={`graha-piece-${p.slug}`}>
      <span className="num text-[13px] font-medium text-ink">{p.subtitle}</span>
      <span className="text-[13px] text-dim">{p.stock_qty} in stock</span>
      <span className="ui text-[11px] text-dim">Tax included</span>
      <span className="num text-[15px] font-medium text-deep">{format(p.price_inr)}</span>
      <ArrowRight className="graha-piece__arrow h-3.5 w-3.5" aria-hidden="true" />
    </Link>
  );
}
